import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Clear as ClearIcon,
    DeleteForever as DeleteForeverIcon,
    Download,
    EditNote as EditNoteIcon,
    MoreHoriz as MoreHorizIcon,
    Plagiarism as PlagiarismIcon,
    RemoveRedEye as RemoveRedEyeIcon,
    Sync,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Snackbar,
    TextField,
    Tooltip,
} from '@mui/material';
import {
    MaterialReactTable,
    MRT_ShowHideColumnsButton,
    MRT_ToggleFiltersButton,
    type MRT_ColumnDef,
    type MRT_RowSelectionState
} from 'material-react-table';
import { MRT_Localization_JA } from 'material-react-table/locales/ja';

import Log from '@/Logger';
import { useDeviceDisplayName } from '@/hooks/useDeviceDisplayName';
import { useDeviceList } from '@/hooks/useDeviceList';
import { useS3FileList } from '@/hooks/useS3FileList';

import { FeatureContext } from '../../FeatureContext';
import { multiSelectionFilter, ItemNameCell } from '../util/ItemSerlectDoropDown/ItemNameFIlter';
import KintoneDialog, { type kintoneApp, TITLE } from '../util/kintone/kintoneAppsDialog';
import { splitDocumentPath, type SplitedObjectKey, splitObjectKey } from '../util/splitObjectKey';
import { useConfirmDialog } from '../util/useConfirmDialog';

import { handleClickDownload, handleClickDownloadAll, registerRecord } from './Common.hooks';
import type { PdfEditProps } from './DocumentList';
import OthersIconDropdown from './OthersIconDropdown';
import { getPresignedUrl, handleClickDelete, handleClickDeleteAll, handleClickPreview, searchTxtList } from './PdfList.hooks';
import { getKintoneToken } from './api';

type Props = {
    path: string
    searchpath: string
    tenantid: string
    setIsVisiblePdfEdit?: React.Dispatch<React.SetStateAction<boolean>>//TODO: テストエラーよけのために一時的に?つけてる
    setPdfEditProp?: React.Dispatch<React.SetStateAction<PdfEditProps>> //TODO: テストエラーよけのために一時的に?つけてる
}

/**
 * ボタンの名前を定義する列挙型
 */
const ButtonName = {
    preview: 'preview',
    download: 'download',
    downloadAll: 'downloadAll',
    deleteAll: 'deleteAll',
    delete: 'delete',
    multiDeviceLink: 'multiDeviceLink',
    others: 'others'
} as const;
type ButtonName = typeof ButtonName[keyof typeof ButtonName];

type ButtonStates = {
    [id: string]: boolean;
}

type PdfRecord = {
    name: string
    multiDeviceLink?: string
    serialId: string
    deviceDisplayName: string
    lastModified: string
    size?: number
    preview?: any
    download?: any
    csv?: any
    delete?: any
    others?: any
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

// ドキュメント一覧のカラム比率
const DOCUMENT_NAME_WIDTH_RATIO_MIN = 0.45;
const DOCUMENT_NAME_WIDTH_RATIO_MAX = 0.55;
const DEVICE_NAME_WIDTH_RATIO_MIN = 0.06;
const DEVICE_NAME_WIDTH_RATIO_MAX = 0.07;
const LAST_MODIFIED_WIDTH_RATIO = 0.07;
const SIZE_WIDTH_RATIO = 0.05;
const EDIT_WIDTH_RATIO = 0.05;
const PREVIEW_WIDTH_RATIO = 0.05;
const OTHER_WIDTH_RATIO = 0.05;

// デフォルト幅の定数
const DEFAULT_DEVICE_NAME_WIDTH = 130;

function getPositiveNumber(val: any): number {
    if (!Number.isFinite(val)) return 0;
    if (val <= 0) return 0;

    return val;
}

function formatSize(originalSize?: number) {
    const size = getPositiveNumber(originalSize);
    const tooltip = `${size.toLocaleString(undefined, { maximumFractionDigits: 2 })} バイト`;

    let view: number;
    let unit: string;

    if (size === 0) {
        view = 0;
        unit = 'KB';
    } else if (size < KB) {
        view = 1;
        unit = 'KB';
    } else if (size < MB) {
        view = size / KB;
        unit = 'KB';
    } else if (size < GB) {
        view = size / MB;
        unit = 'MB';
    } else {
        view = size / GB;
        unit = 'GB';
    }

    return <Tooltip title={tooltip} arrow><span>{`${view.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`}</span></Tooltip>;
}

/**
 * 本フラグはテスト時にsetFlag関数を使用し、trueにしてください。
 * テスト時にrenderでPdfListを取得しようとしたとき、columnsの要素(プレビュー、ダウンロード、削除など)の最後の1要素が取得できない現象が発生しています。
 * 暫定処理として本フラグをtrueにすることで、後の処理で空の要素を１つ追加しています。
 * 根本原因として、現状使用しているライブラリが古いためであると考えており、このPdfList.tsxの再設計を行う必要があると考えています。
 * しかし、2025年3月現在、PdfList.tsxの再設計を行う工数を取ることができず、本暫定処理を負債として抱えることにしました。
 */
let testFlag = false;
export function setFlag(bool: boolean) {
    testFlag = bool
}

const PdfList = (props: Props) => {
    const logger = Log.getLogger();
    const tableRef = useRef<HTMLDivElement>(null);
    // カスタムフックを使用してPDFデータを管理
    const { list, isFetching: isPdfRecordsFetching, isLoading: isPdfRecordsLoading, refetch: refetchPdfList, setExternalSearchList } = useS3FileList('PdfRecord', props.path);
    // デバイス表示名を管理するカスタムフック
    const { getDisplayName, isLoading: isDeviceDisplayNameLoading } = useDeviceDisplayName();

    const { deviceList } = useDeviceList();
    const getDeviceId = useCallback((serialId: string) => {
        const deviceId = deviceList.find(i => (i.serialId === serialId))?.deviceId
        if (!deviceId)
            throw new Error(`Device with serialId ${serialId} not found`);
        return deviceId;
    }, [deviceList]);

    // 読み込み中状態を定義
    const isLoading = list === undefined || !tableRef.current || isPdfRecordsLoading || isDeviceDisplayNameLoading;

    // デバイス表示名を付与したデータを生成
    const data = useMemo(() => {
        return list?.map((record) => ({
            ...record,
            deviceDisplayName: getDisplayName(record.serialId),
        })) ?? [];
    }, [list, getDisplayName]);

    const [showSearchBox, setShowSearchBox] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarText, setSnackBarText] = React.useState('');
    const [snackBarType, setSnackBarType] = React.useState<any>(undefined);

    //リロード連打防止用のステート
    const [isButtonActive, setIsButtonActive] = React.useState(true);
    //プレビュー、ダウンロードCSV連打防止用ステート
    const [buttonStates, setButtonStates] = React.useState<ButtonStates>({});
    // kintoneのアプリを選択するドロップダウンの表示/非表示状態を保持します
    const [isKintoneDialogOpen, setIsKintoneDialogOpen] = useState<boolean>(false);
    // メニューの状態と、クリックされたアイコンのアンカー要素を管理
    const [isOthersDropdownOpen, setIsOthersDropdownOpen] = useState<boolean>(false);
    // その他が押されている文書の名前を格納する
    const [othersSelectedItemOriginalName, setOthersSelectedItemOriginalName] = useState<string>('');
    // その他が押されたときに表示されるドロップダウンの表示位置を決めるその他ボタンの要素を保持します
    const [anchorOthersButtonEl, setAnchorOthersButtonEl] = useState<HTMLElement | null>(null);

    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog();

    const [selectedDeviceList, setSelectedDeviceList] = React.useState<string[]>([]);

    const handleSnackBarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackBarOpen(false);
    };

    const searchBoxStyle: React.CSSProperties = {
        marginTop: '-24px',
        height: '10px',
        width: showSearchBox ? '280px' : '0',
        opacity: showSearchBox ? 1 : 0,
        transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out',
        visibility: showSearchBox ? 'visible' : 'hidden',
    };

    const handleSearchInPdfIconClick = () => {
        logger.ui.info('PdfList', 'handleSearchInPdfIconClick');
        setShowSearchBox(!showSearchBox);
    };

    const handleSearchInPdfCloseClick = () => {
        setShowSearchBox(false);
        setExternalSearchList(null);
        refetchPdfList(); // カスタムフックのrefetchを使用
    };

    const handleClearClick = () => {
        setSearchText('');
        setExternalSearchList(null);
        refetchPdfList(); // カスタムフックのrefetchを使用
    };

    const handleSearch = () => {
        logger.ui.info('PdfList', 'handleSearch');
        searchTxtList(props.tenantid, searchText, props.path)
            .then((data) => {
                setExternalSearchList(data);
            })
            .catch(error => {
                logger.ui.error('PdfList', 'handleSearch', `Error:${JSON.stringify(error)}`);
            });
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            // Enterキーが押されたときにも検索
            handleSearch();
        }
    };

    useEffect(() => {
        if (showSearchBox && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showSearchBox]);

    const handleClickDebounce = (name: any, key: string, id: string) => {
        //IDをもとにステートを更新
        setButtonStates((prevButtonStates) => ({
            ...prevButtonStates,
            [id]: true,
        }));
        handleClickWithDebounce(name, key, id);
    };

    const handleClickWithDebounce = async (name: any, key: string, id: any) => {
        try {
            switch (key) {
                case ButtonName.preview:
                    await handleClickPreview(name);
                    break;
                case ButtonName.download:
                    await handleClickDownload(name);
                    break;
                case ButtonName.downloadAll:
                    setRowSelection({});
                    await handleClickDownloadAll(name);
                    break;
                case ButtonName.deleteAll:
                    const deleteListLen = name.length
                    setConfirmDialogMessage([
                        `${deleteListLen}個のファイルを削除します。`,
                        '削除されたドキュメントは元に戻せません。',
                    ], true);

                    const resultDeleteAll = await openConfirmDialog();

                    if (resultDeleteAll === 'confirm') {
                        logger.ui.info('PdfList', 'delete all', 'Start to delete Allfiles');
                        let resultDeleteAll = await handleClickDeleteAll(name);

                        if (resultDeleteAll.length !== 0) {
                            for (let error of resultDeleteAll) {
                                const err = error as Error
                                logger.ui.error('PdfList', 'delete all', 'Fail to delete files:' + err);
                            }
                            setSnackBarType('error');
                            setSnackBarText(`${resultDeleteAll.length}個のファイルの削除要求に失敗しました`);
                            setSnackBarOpen(true);
                        } else {
                            logger.ui.info('PdfList', 'delete all', 'Succeed to delete files');
                            setSnackBarType('success');
                            setSnackBarText(`${deleteListLen}個のファイルの削除要求に成功しました`);
                            setSnackBarOpen(true);
                        }
                        logger.ui.info('PdfList', 'reflesh');
                        setRowSelection({});
                        refetchPdfList(); // カスタムフックのrefetchを使用
                    }
                    break;

                case ButtonName.delete: {
                    const row = data.find(record => record.name === name);
                    const splitedObjectKey: SplitedObjectKey = splitObjectKey(name);
                    const deviceDisplayName: string = row?.deviceDisplayName ?? splitedObjectKey.serialId;
                    setConfirmDialogMessage([
                        `${deviceDisplayName} から ${splitedObjectKey.documentName} を削除します。`,
                        '削除されたドキュメントは元に戻せません。',
                    ], true);

                    const result = await openConfirmDialog();
                    if (result === 'confirm') {
                        logger.ui.info('PdfList', ButtonName.delete, 'Send selected');
                        try {
                            logger.ui.info('PdfList', ButtonName.delete, 'Start to delete file');
                            const resp = await handleClickDelete(name);
                            logger.ui.info('PdfList', ButtonName.delete, 'Succeed to delete file');
                            setSnackBarType('success');
                            setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(resp.documentName).documentName} の削除要求に成功しました`);
                            setSnackBarOpen(true);
                            logger.ui.info('PdfList', 'reflesh');
                            setRowSelection({});
                            refetchPdfList(); // カスタムフックのrefetchを使用 
                        } catch (e) {
                            logger.ui.error('PdfList', ButtonName.delete, 'Fail to delete file');
                            const err = e as { serialId: string, documentName: string, err: Error }
                            setSnackBarType('error');
                            setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(err.documentName).documentName} の削除要求に失敗しました`);
                            setSnackBarOpen(true);
                        }
                    }
                    logger.ui.info('PdfList', ButtonName.delete, 'Cancel selected');
                } break;
                case ButtonName.multiDeviceLink: {
                    const row = data.find(record => record.name === name);
                    const splitedObjectKey: SplitedObjectKey = splitObjectKey(name);
                    const deviceDisplayName: string = row?.deviceDisplayName ?? splitedObjectKey.serialId;
                    const selectedDeviceId = getDeviceId(splitedObjectKey.serialId);
                    props.setPdfEditProp?.({
                        docId: splitedObjectKey.documentId,
                        serialId: splitedObjectKey.serialId,
                        deviceId: selectedDeviceId,
                        deviceName: deviceDisplayName,
                    });
                    props.setIsVisiblePdfEdit?.(true);

                } break;
                case ButtonName.others:
                    break;
            }
        } catch (error) {
            logger.ui.error('PdfList', 'handleClickWithDebounce', `Error:${JSON.stringify(error)}`);
        } finally {
            setButtonStates((prevButtonStates) => ({
                ...prevButtonStates,
                [id]: false,
            }));
        }
    };

    // KintoneDialogでkintoneアプリが選択された後の処理を記述します
    const handleKintoneDialogSelected = async (selectedItem: kintoneApp[]): Promise<void> => {

        // kintoneにレコード登録をします
        // レコードに登録をするフィールドを持つPDFのpresignedURLを取得します
        const presignedURL = await getPresignedUrl(othersSelectedItemOriginalName);
        if (presignedURL === '') {
            logger.ui.error('PdfList', 'handleKintoneDialogSelected', 'Fail to get presigned URL');
            setSnackBarType('error');
            setSnackBarText('ドキュメントの取得に失敗しました');
            setSnackBarOpen(true);
            return;
        }

        // kintoenアプリにレコード登録するためにOAuth2.0のトークンをとエンドポイント取得します
        const resp = await getKintoneToken();
        const url = new URL(resp.endpoint);

        // kintoneのトークンが取得できていることをチェックする
        if (resp.token === '') {
            logger.ui.error('PdfList', 'handleKintoneDialogSelected', 'Fail to get token');
            setSnackBarType('error');
            setSnackBarText('kintoneの認証情報取得に失敗しました');
            setSnackBarOpen(true);
            return;
        }

        // kintonのドメインが取得できていることをチェックする
        if (url.host === '') {
            logger.ui.error('PdfList', 'handleKintoneDialogSelected', 'Fail to get kintone domain');
            setSnackBarType('error');
            setSnackBarText('kintoneのトークンエンドポイントの取得に失敗しました');
            setSnackBarOpen(true);
            return;
        }

        // kintoneのREST APIに対してリクエストをレコード登録のリクエストを送ります
        const result = await registerRecord(presignedURL, url.host, selectedItem[0].id.toString(), "OAuth2.0", resp.token);
        if (!result) {
            logger.ui.info('PdfList', 'handleKintoneDialogSelected', 'Fail to register record');
            setSnackBarType('error');
            setSnackBarText('レコード登録に失敗しました。ドキュメントとアプリが一致していないか、必須項目が未入力、またはkintoneアプリのフィールドの既定値が英数字以外となっていることが原因として想定されます。');
            setSnackBarOpen(true);
            return;
        }

        logger.ui.info('PdfList', 'handleKintoneDialogSelected', 'Success to register record');
        setSnackBarType('success');
        setSnackBarText('レコード登録に成功しました');
        setSnackBarOpen(true);
        return
    };

    function getDocumentName(objectKey: string) {
        const splited = splitObjectKey(objectKey);
        return splited.documentName;
    }

    const columns: MRT_ColumnDef<PdfRecord>[] = [
        {
            accessorKey: 'name',
            header: 'ファイル名',
            minSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DOCUMENT_NAME_WIDTH_RATIO_MIN,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DOCUMENT_NAME_WIDTH_RATIO_MAX,
            Cell: ({ cell }) => <span>{getDocumentName(cell.getValue<string>())}</span>,
            sortingFn: (a, b) => {
                const aName = splitObjectKey(a.original.name).documentName;
                const bName = splitObjectKey(b.original.name).documentName;
                if (aName === bName) return 0;
                if (aName < bName) return -1;
                return 1;
            },
            filterFn: 'includesString'
        },

        {
            accessorKey: 'deviceDisplayName',
            header: 'デバイス名',
            minSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO_MIN,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO_MAX,
            // ItemNameCellによる省略表示とツールチップ機能
            Cell: ({ cell }) => (
                <ItemNameCell
                    itemName={cell.getValue<string>()}
                    width={tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO_MAX}
                />
            ),
            Filter: ({ column }) => {
                const deviceNames = Array.from(new Set<string>(
                    data.map((record) => record.deviceDisplayName)
                ));
                const width = tableRef.current?.clientWidth ? tableRef.current.clientWidth * DEVICE_NAME_WIDTH_RATIO_MAX : DEFAULT_DEVICE_NAME_WIDTH;
                return multiSelectionFilter<PdfRecord>(
                    column,
                    deviceNames,
                    selectedDeviceList,
                    setSelectedDeviceList,
                    width
                );
            },
            filterFn: (row, _columnIds, filterValue) => {
                return filterValue.includes(row.getValue('deviceDisplayName'));
            },
        },
        {
            accessorKey: 'lastModified',
            header: '更新日時',
            minSize: 148,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * LAST_MODIFIED_WIDTH_RATIO,
        },
        {
            accessorKey: 'size',
            header: 'サイズ',
            muiTableHeadCellProps: {
                align: 'left',
            },
            muiTableBodyCellProps: {
                align: 'right',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * SIZE_WIDTH_RATIO,
            Cell: ({ cell }) => formatSize(cell.getValue<number>()),
            sortingFn: (a, b) => (a.original.size ?? 0) - (b.original.size ?? 0),
        },

        {
            accessorKey: ButtonName.multiDeviceLink,
            header: '編集',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * EDIT_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name="multiDeviceLink"
                    edge="end"
                    onClick={event => {
                        event.stopPropagation() // クリックイベントを親に伝播しないように止める
                        logger.ui.info('PdfList', ButtonName.multiDeviceLink);
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = ButtonName.multiDeviceLink + row.id;
                        const id = event.currentTarget.id as string;
                        handleClickDebounce(row.original.name, ButtonName.multiDeviceLink, id);
                    }}
                    size="small"
                    disabled={buttonStates[ButtonName.multiDeviceLink + row.id]}
                >
                    <EditNoteIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },

        {
            accessorKey: ButtonName.preview,
            header: 'プレビュー',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * PREVIEW_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name="preview"
                    edge="end"
                    onClick={event => {
                        event.stopPropagation() // クリックイベントが親に伝播しないように止める
                        logger.ui.info('PdfList', ButtonName.preview);
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = ButtonName.preview + row.id;
                        const id = event.currentTarget.id as string;
                        handleClickDebounce(row.original.name, ButtonName.preview, id);
                    }}
                    size="small"
                    disabled={buttonStates[ButtonName.preview + row.id]}
                >
                    <RemoveRedEyeIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: ButtonName.download,
            header: 'ダウンロード',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * OTHER_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name={ButtonName.download}
                    edge="end"
                    onClick={event => {
                        event.stopPropagation() // クリックイベントが親に伝播しないように止める
                        logger.ui.info('PdfList', ButtonName.download, `File size is ${row.original.size}`);
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = ButtonName.download + row.id;
                        const id = event.currentTarget.id as string;
                        handleClickDebounce(row.original.name, ButtonName.download, id);
                    }}
                    size="small"
                    disabled={buttonStates[ButtonName.download + row.id]}
                >
                    <Download fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: ButtonName.delete,
            header: '削除',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * 0.05,
            Cell: ({ row }) => (
                <IconButton
                    name="delete"
                    edge="end"
                    onClick={event => {
                        event.stopPropagation() // クリックイベントを親に伝播しないように止める
                        logger.ui.info('PdfList', ButtonName.delete);
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = ButtonName.delete + row.id;
                        const id = event.currentTarget.id as string;
                        handleClickDebounce(row.original.name, ButtonName.delete, id);
                    }}
                    size="small"
                    disabled={buttonStates[ButtonName.delete + row.id]}
                >
                    <DeleteForeverIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: ButtonName.others,
            header: 'その他',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * 0.05,
            Cell: ({ row }) => (
                <IconButton
                    name="others"
                    edge="end"
                    onClick={event => {
                        event.stopPropagation() // クリックイベントを親に伝播しないように止める
                        logger.ui.info('PdfList', ButtonName.others, 'clicked');
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = ButtonName.others + row.id;
                        const id = event.currentTarget.id as string;
                        setOthersSelectedItemOriginalName(row.original.name);
                        setIsOthersDropdownOpen(true);
                        setAnchorOthersButtonEl(event.currentTarget);
                        handleClickDebounce(row.original.name, ButtonName.others, id);
                    }}
                    size="small"
                    disabled={buttonStates[ButtonName.others + row.id]}
                >
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
    ];

    const features = useContext(FeatureContext);

    // 権限に応じて機能のオンオフを切り替える
    if (!features.deleteDocument()) {
        const idx = columns.findIndex(col => col.accessorKey === ButtonName.delete);
        if (idx !== -1) columns.splice(idx, 1);
    }
    if (!features.multiDeviceLink()) {
        const idx = columns.findIndex(col => col.accessorKey === ButtonName.multiDeviceLink);
        if (idx !== -1) columns.splice(idx, 1);
    }

    if (testFlag) {
        columns.push({} as any)
    }
    return (
        <>
            <MaterialReactTable
                columns={columns}
                data={data}
                positionPagination={'top'}
                enablePagination={false}
                enableFullScreenToggle={false}
                enableStickyHeader
                enableDensityToggle={false}
                enableGlobalFilterModes
                enableRowSelection
                getRowId={(row) => row.name}
                onRowSelectionChange={setRowSelection}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: row.getToggleSelectedHandler(),
                    sx: { cursor: 'pointer' },
                })}
                state={{ rowSelection, isLoading }}
                muiLinearProgressProps={{
                    color: 'primary',
                    sx: { height: '3px' },
                }}
                initialState={{
                    density: 'compact',
                    sorting: [{
                        id: 'lastModified',
                        desc: true
                    }],
                    showGlobalFilter: true,
                    showColumnFilters: true,
                }}
                muiTableContainerProps={{ ref: tableRef, sx: { height: 'calc(100vh - 189px)', width: 'calc(100vw - 57px)' }, style: { overflowX: 'auto', overflowY: 'scroll' } }}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                        width: '100%',
                        height: '100%',
                    },
                }}
                muiTableBodyCellProps={{ style: { whiteSpace: 'normal' } }} // ファイル名が長い場合に横スクロールではなく改行で対応する
                enableBottomToolbar={false}
                localization={MRT_Localization_JA}
                renderToolbarInternalActions={({ table }) => (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                label=""
                                variant="standard"
                                size="small"
                                inputRef={inputRef}
                                value={searchText}
                                placeholder="ファイル内の文字を検索"
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                style={searchBoxStyle}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClearClick} style={{ color: 'silver' }}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton onClick={handleSearch}>
                                                <PlagiarismIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>

                        <Tooltip title="選択されたファイルをダウンロード" arrow>
                            <span>
                                <IconButton
                                    name={ButtonName.downloadAll}
                                    edge='end'
                                    size='medium'
                                    style={{ marginRight: '0px' }}
                                    disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                                    onClick={(event) => {
                                        const fileNamesSelected = Object.keys(rowSelection)
                                        logger.ui.info('PdfList', ButtonName.downloadAll);
                                        //クリック時に押下したボタンにIDを付与
                                        handleClickDebounce(fileNamesSelected, ButtonName.downloadAll, ButtonName.downloadAll)
                                    }}
                                    data-testid="download-all"
                                >
                                    <Download />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {features.deleteDocument() ?
                            <Tooltip title="選択されたファイルを削除" arrow>
                                <span>
                                    <IconButton
                                        name={ButtonName.deleteAll}
                                        edge='end'
                                        size='medium'
                                        style={{ marginRight: '0px' }}
                                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}//選択時以外ボタンを無効に
                                        onClick={(event) => {
                                            const fileNamesSelected = Object.keys(rowSelection)
                                            logger.ui.info('PdfList', ButtonName.deleteAll);
                                            //クリック時に押下したボタンにIDを付与
                                            handleClickDebounce(fileNamesSelected, ButtonName.deleteAll, ButtonName.deleteAll);
                                        }}
                                        data-testid="delete-all"
                                    >
                                        <DeleteForeverIcon />
                                    </IconButton>
                                </span>
                            </Tooltip> : undefined}

                        <Tooltip title="再読み込み" arrow>
                            <IconButton
                                edge='end'
                                onClick={() => {
                                    if (isButtonActive && !isPdfRecordsFetching) { // ローディング中は無効化
                                        logger.ui.info('PdfList', 'reflesh');
                                        setRowSelection({});
                                        setIsButtonActive(false);
                                        refetchPdfList(); // カスタムフックのrefetchを使用
                                        // refetchは非同期なので、連打防止のため少し待ってから有効化
                                        setTimeout(() => setIsButtonActive(true), 1000);
                                    }
                                }}
                                size='medium'
                                style={{ marginRight: '0px' }}
                                disabled={!isButtonActive || isPdfRecordsFetching}
                            >
                                {isPdfRecordsFetching ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <Sync fontSize='medium' />
                                )}
                            </IconButton>
                        </Tooltip>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {showSearchBox ? (
                                <>
                                    <IconButton onClick={handleSearchInPdfCloseClick}>
                                        <PlagiarismIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <Tooltip title="ファイル内の文字を検索" arrow>
                                    <IconButton onClick={handleSearchInPdfIconClick}>
                                        <PlagiarismIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <MRT_ToggleFiltersButton table={table} />
                        <MRT_ShowHideColumnsButton table={table} />
                    </Box>
                )}
            />
            <OthersIconDropdown
                open={isOthersDropdownOpen}
                anchorInfo={anchorOthersButtonEl}
                onClose={() => {
                    setIsOthersDropdownOpen(false);
                    setAnchorOthersButtonEl(null);
                }}
                onSelected={(itemName) => {
                    if (itemName === 'kintoneRecordRegister') {
                        setIsKintoneDialogOpen(true)
                    }
                }}
            />
            <Snackbar open={snackBarOpen} onClose={handleSnackBarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackBarClose} severity={snackBarType} sx={{ width: '100%' }} >
                    {snackBarText}
                </Alert>
            </Snackbar>
            <ConfirmDialog title='ドキュメントを削除しますか？' />
            <KintoneDialog
                open={isKintoneDialogOpen}
                onClose={() => { setIsKintoneDialogOpen(false) }}
                onSelect={async (selectedItem) => { handleKintoneDialogSelected(selectedItem) }}
                maxSelectableNum={1}
                dialogTitle={TITLE}
                dialogDescription="レコードを登録するアプリを選択してください"
            />
        </>
    );
};

export default PdfList;